import React, { useMemo, useState, useEffect } from 'react';
import { StyleSheet, View, TouchableOpacity, ScrollView, ActivityIndicator } from 'react-native';
import {
  Portal,
  Modal,
  Text,
  TextInput,
  Button,
  Avatar,
  useTheme,
} from 'react-native-paper';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useFonts } from 'expo-font';
import {
  AVATAR_ICON_OPTIONS,
  AVATAR_TYPES,
  normalizeAvatar,
  DEFAULT_AVATAR_OPTION,
} from '../constants/avatars';

const MATERIAL_COMMUNITY_ICONS_TTF = require('@expo/vector-icons/build/vendor/react-native-vector-icons/Fonts/MaterialCommunityIcons.ttf');

export default function ProfileModal({
  visible,
  profile,
  onChangeProfile,
  onDismiss,
  onSave,
  tokens = 0,
  onShowRewardedAd,
  isRewardedLoading = false,
  nicknameLocked = false,
}) {
  const theme = useTheme();
  const [customEmoji, setCustomEmoji] = useState('');
  const [fontsLoaded, fontsError] = useFonts({
    MaterialCommunityIcons: MATERIAL_COMMUNITY_ICONS_TTF,
  });

  useEffect(() => {
    MaterialCommunityIcons.loadFont().catch((error) => {
      if (__DEV__) {
        console.warn('MaterialCommunityIcons font load failed:', error);
      }
    });
  }, []);

  const initials = useMemo(() => {
    if (profile?.nickname) {
      return profile.nickname
        .split(' ')
        .map(word => word[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();
    }
    return '👤';
  }, [profile?.nickname]);

  // Icon to emoji mapping
  const iconToEmoji = {
    'headphones': '🎧',
    'microphone-variant': '🎤',
    'record-player': '💿',
    'radio': '📻',
    'album': '💿',
    'guitar-electric': '🎸',
    'drum': '🥁',
    'music-note': '🎵',
    'music-circle': '🎶',
    'music-clef-treble': '🎼',
    'music-rest-whole': '🎵',
    'disc': '💿',
    'equalizer': '🎚️',
    'metronome': '⏱️',
    'cassette': '📼',
    'boombox': '📻',
    'tune-vertical': '🎛️',
    'rocket-launch': '🚀',
    'star-four-points': '⭐',
    'fire': '🔥',
    'turntable': '💿',
    'lightning-bolt': '⚡',
    'waveform': '〰️',
    'music-box': '🎵',
    'play-circle': '▶️',
    'music-note-plus': '🎵',
    'music-note-off': '🎵',
    'music-note-eighth': '🎵',
    'music-note-sixteenth': '🎵',
    'music-note-half': '🎵',
    'music-note-quarter': '🎵',
    'music-note-whole': '🎵',
    'music-note-multiple': '🎵',
    'music-circle-outline': '🎶',
    'karate': '🥋',
    'emoticon-cool-outline': '😎',
    'gamepad-variant': '🎮',
    'controller-classic': '🎮',
    'virtual-reality': '🥽',
    'robot-excited-outline': '🤖',
    'rocket-launch-outline': '🚀',
    'piano': '🎹',
    'violin': '🎻',
  };

  const getEmojiForIcon = (iconName) => {
    return iconToEmoji[iconName] || '🎵';
  };

  const validIconOptions = useMemo(() => {
    // Tüm seçenekleri gösteriyoruz, emoji kullanacağız
    return AVATAR_ICON_OPTIONS.length > 0 ? AVATAR_ICON_OPTIONS : [DEFAULT_AVATAR_OPTION];
  }, []);

  const selectedAvatar = useMemo(() => {
    const normalized = normalizeAvatar(profile?.avatar || DEFAULT_AVATAR_OPTION);
    return normalized;
  }, [profile?.avatar]);

  const handleSelectIcon = (option) => {
    onChangeProfile({
      ...profile,
      avatar: {
        type: AVATAR_TYPES.ICON,
        id: option.id,
      },
    });
  };

  const handleSelectEmoji = (emoji) => {
    onChangeProfile({
      ...profile,
      avatar: {
        type: AVATAR_TYPES.EMOJI,
        value: emoji,
      },
    });
  };

  return (
    <Portal>
      <Modal
        visible={visible}
        onDismiss={onDismiss}
        contentContainerStyle={[styles.modalContainer, { backgroundColor: theme.colors.background }]}
      >
        <View style={styles.fullscreenContainer}>
          <ScrollView
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            <View style={styles.headerSection}>
              <Text style={styles.title}>Profilini Özelleştir</Text>
              {selectedAvatar.type === AVATAR_TYPES.ICON ? (
                <View
                  style={[
                    styles.avatarPreviewIcon,
                    {
                      width: 96,
                      height: 96,
                      borderRadius: 48,
                      backgroundColor: selectedAvatar.backgroundColor,
                      justifyContent: 'center',
                      alignItems: 'center',
                    },
                  ]}
                >
                  <Text style={{ fontSize: 48 }}>{getEmojiForIcon(selectedAvatar.icon)}</Text>
                </View>
              ) : (
                <Avatar.Text
                  size={96}
                  label={selectedAvatar.value || initials}
                  style={styles.avatarPreviewText}
                  labelStyle={styles.avatarPreviewLabel}
                />
              )}
              <Text style={styles.avatarText}>
                Chat odalarında görünmek için sembolünü seç.
              </Text>
            </View>

            <View style={styles.nicknameSection}>
              <TextInput
                mode="outlined"
                label="Kullanıcı adı"
                value={profile?.nickname || ''}
                onChangeText={(text) => onChangeProfile({ ...profile, nickname: text })}
                style={styles.input}
                editable={!nicknameLocked}
                disabled={nicknameLocked}
              />
              <Text style={styles.helperText}>
                {nicknameLocked
                  ? 'Kullanıcı adın kilitlendi. Avatarını değiştirmeye devam edebilirsin.'
                  : 'Kullanıcı adını bir kez değiştirebilirsin, sonra kilitlenecek.'}
              </Text>
            </View>

            <View style={styles.tokensSection}>
              <View style={styles.tokensHeader}>
                <Text style={styles.tokensLabel}>Sosyal Enerji</Text>
                <Text style={styles.tokensValue}>{tokens}</Text>
              </View>
              <View style={styles.energyBar}>
                {Array.from({ length: 10 }).map((_, idx) => {
                  const filled = idx < Math.min(tokens, 10);
                  return (
                    <View
                      key={idx}
                      style={filled ? styles.energySegmentFilled : styles.energySegmentEmpty}
                    />
                  );
                })}
              </View>
              <Button
                mode="contained"
                onPress={onShowRewardedAd}
                style={styles.rewardButton}
                labelStyle={styles.rewardButtonLabel}
                buttonColor="#2ECC71"
                textColor="#0B2A16"
                icon="gift"
                loading={isRewardedLoading}
                disabled={isRewardedLoading}
              >
                Reklam İzle • +1 Enerji
              </Button>
            </View>

            <Text style={styles.sectionTitle}>Sticker seç</Text>
            <View style={styles.avatarGrid}>
              {validIconOptions.map((option) => {
                const isSelected =
                  selectedAvatar.type === AVATAR_TYPES.ICON && selectedAvatar.id === option.id;
                const emoji = getEmojiForIcon(option.icon);
                return (
                  <TouchableOpacity
                    key={option.id}
                    style={[styles.avatarOption, isSelected && styles.avatarOptionSelected]}
                    activeOpacity={0.8}
                    onPress={() => handleSelectIcon(option)}
                  >
                    <View
                      style={[
                        styles.avatarOptionIcon,
                        { backgroundColor: option.backgroundColor },
                        isSelected && styles.avatarOptionIconSelected,
                        {
                          width: 44,
                          height: 44,
                          borderRadius: 22,
                          justifyContent: 'center',
                          alignItems: 'center',
                        },
                      ]}
                    >
                      <Text style={{ fontSize: 24 }}>{emoji}</Text>
                    </View>
                  </TouchableOpacity>
                );
              })}
            </View>

            <Text style={styles.sectionTitle}>Emoji seç</Text>
            <View style={styles.emojiRow}>
              {['🎧','🎵','🎶','🎤','🎸','🎷','🎺','🥁','🎹','⭐','🔥','🚀'].map(em => (
                <TouchableOpacity
                  key={em}
                  style={[styles.emojiChip, selectedAvatar.type === AVATAR_TYPES.EMOJI && selectedAvatar.value === em && styles.emojiChipSelected]}
                  onPress={() => handleSelectEmoji(em)}
                  activeOpacity={0.8}
                >
                  <Text style={styles.emojiText}>{em}</Text>
                </TouchableOpacity>
              ))}
            </View>

            <View style={styles.customEmojiRow}>
              <TextInput
                mode="outlined"
                label="Özel Emoji"
                value={customEmoji}
                onChangeText={setCustomEmoji}
                placeholder="Örn: 🙂"
                style={styles.customEmojiInput}
              />
              <Button
                mode="contained"
                onPress={() => {
                  if (customEmoji?.trim()) {
                    handleSelectEmoji(customEmoji.trim());
                    setCustomEmoji('');
                  }
                }}
                style={styles.customEmojiButton}
              >
                Seç
              </Button>
            </View>
          </ScrollView>

          <View style={styles.buttonRow}>
            <Button mode="text" onPress={onDismiss}>
              İptal
            </Button>
            <Button
              mode="contained"
              onPress={onSave}
              disabled={!profile?.nickname?.trim()}
              style={[styles.saveButton, styles.buttonSpacing]}
            >
              Kaydet
            </Button>
          </View>
        </View>
      </Modal>
    </Portal>
  );
}

const styles = StyleSheet.create({
  modalContainer: {
    flex: 1,
    margin: 0,
    padding: 0,
    borderRadius: 0,
  },
  fullscreenContainer: {
    flex: 1,
    paddingHorizontal: 20,
    paddingTop: 32,
    paddingBottom: 16,
  },
  title: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#FF4444',
    marginBottom: 24,
    textAlign: 'center',
  },
  headerSection: {
    alignItems: 'center',
    marginBottom: 24,
  },
  avatarPreviewIcon: {
    borderRadius: 24,
    marginBottom: 12,
  },
  avatarPreviewText: {
    backgroundColor: '#FFFFFF',
    borderWidth: 4,
    borderColor: '#FF4444',
    marginBottom: 12,
  },
  avatarPreviewLabel: {
    fontSize: 36,
  },
  avatarText: {
    marginTop: 12,
    fontSize: 12,
    color: '#B0B0B0',
    textAlign: 'center',
  },
  input: {
    marginBottom: 8,
    backgroundColor: '#2C2C2C',
  },
  nicknameSection: {
    marginBottom: 24,
  },
  helperText: {
    fontSize: 12,
    color: '#B0B0B0',
    marginTop: 6,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 12,
    color: '#FFFFFF',
  },
  avatarGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    paddingBottom: 24,
  },
  emojiRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 16,
  },
  emojiChip: {
    backgroundColor: '#1B1F2A',
    borderWidth: 1,
    borderColor: '#2C3446',
    borderRadius: 10,
    paddingVertical: 6,
    paddingHorizontal: 10,
    marginRight: 8,
    marginBottom: 8,
  },
  emojiChipSelected: {
    borderColor: '#E94560',
    backgroundColor: '#202835',
  },
  emojiText: {
    fontSize: 18,
    color: '#E6EAF2',
  },
  avatarOption: {
    width: '22%',
    alignItems: 'center',
    marginBottom: 16,
  },
  avatarOptionSelected: {
    transform: [
      { scale: 1.05 },
    ],
  },
  avatarOptionIcon: {
    borderRadius: 16,
  },
  avatarOptionIconSelected: {
    borderWidth: 2,
    borderColor: '#FFFFFF',
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 8,
  },
  buttonSpacing: {
    marginLeft: 12,
  },
  scrollContent: {
    paddingBottom: 32,
  },
  tokensSection: {
    marginBottom: 24,
    padding: 18,
    backgroundColor: '#1B1F2A',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#2C3446',
  },
  energyBar: {
    flexDirection: 'row',
    gap: 2,
    marginBottom: 14,
  },
  energySegmentFilled: {
    width: 16,
    height: 10,
    borderRadius: 2,
    backgroundColor: '#00C2A8',
  },
  energySegmentEmpty: {
    width: 16,
    height: 10,
    borderRadius: 2,
    backgroundColor: '#222835',
  },
  tokensHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  tokensLabel: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  tokensValue: {
    fontSize: 26,
    fontWeight: 'bold',
    color: '#FF4444',
  },
  rewardButton: {
    borderRadius: 12,
    paddingVertical: 4,
  },
  rewardButtonLabel: {
    fontSize: 14,
    fontWeight: 'bold',
  },
  saveButton: {
    borderRadius: 12,
  },
  customEmojiRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  customEmojiInput: {
    flex: 1,
    backgroundColor: '#2C3446',
  },
  customEmojiButton: {
    borderRadius: 10,
  },
});
